import * as i0 from '@angular/core';
import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';

class Point {
    constructor(x, y) {
        this.x = x;
        this.y = y;
    }
    static create(x, y) {
        return new Point(x, y);
    }
    clone() {
        return Point.create(this.x, this.y);
    }
    set(x, y) {
        this.x = x;
        this.y = y;
        return this;
    }
    translate(offsetX, offsetY) {
        this.x += offsetX;
        this.y += offsetY;
        return this;
    }
    translateX(offsetX) {
        return this.translate(offsetX, 0);
    }
    translateY(offsetY) {
        return this.translate(0, offsetY);
    }
    scale(factor) {
        this.x *= factor;
        this.y *= factor;
        return this;
    }
    map(fn) {
        this.x = fn.call(this, this.x);
        this.y = fn.call(this, this.y);
        return this;
    }
    isZero() {
        return this.equals(Point.create(0, 0));
    }
    equals(other) {
        return this.x === other.x && this.y === other.y;
    }
    toString() {
        return `(${this.x}, ${this.y})`;
    }
}

class Overflow {
    constructor(left, top, right, bottom) {
        this.left = left;
        this.top = top;
        this.right = right;
        this.bottom = bottom;
    }
    static create(left, top, right, bottom) {
        return new Overflow(left, top, right, bottom);
    }
    equals(other) {
        return this.left === other.left &&
            this.top === other.top &&
            this.bottom === other.bottom &&
            this.right === other.right;
    }
    toString() {
        return `(${this.left}, ${this.top}, ${this.right}, ${this.bottom})`;
    }
}

class Rectangle {
    constructor(left, top, right, bottom) {
        this.left = left;
        this.top = top;
        this.right = right;
        this.bottom = bottom;
    }
    static create(x, y, width, height) {
        return new Rectangle(x, y, x + width, y + height);
    }
    static fromBounds(left, top, right, bottom) {
        return Rectangle.create(left, top, right - left, bottom - top);
    }
    static fromRect(rect) {
        return Rectangle.create(rect.x, rect.y, rect.width, rect.height);
    }
    static empty() {
        return Rectangle.create(0, 0, 0, 0);
    }
    get x() {
        return this.left;
    }
    set x(newX) {
        const offsetX = newX - this.x;
        this.left = newX;
        this.right += offsetX;
    }
    get y() {
        return this.top;
    }
    set y(newY) {
        const offsetY = newY - this.y;
        this.top = newY;
        this.bottom += offsetY;
    }
    get width() {
        return this.right - this.left;
    }
    set width(newWidth) {
        this.right = this.left + newWidth;
    }
    get height() {
        return this.bottom - this.top;
    }
    set height(newWidth) {
        this.bottom = this.top + newWidth;
    }
    clone() {
        return Rectangle.create(this.x, this.y, this.width, this.height);
    }
    setRectangle(x, y, width, height) {
        this.left = x;
        this.top = y;
        this.right = x + width;
        this.bottom = y + height;
        return this;
    }
    setBounds(left, top, right, bottom) {
        this.left = left;
        this.top = top;
        this.right = right;
        this.bottom = bottom;
        return this;
    }
    copyFrom(other) {
        this.left = other.left;
        this.top = other.top;
        this.right = other.right;
        this.bottom = other.bottom;
        return this;
    }
    moveTo(p, anchor = this.leftTop()) {
        this.x = p.x + (this.x - anchor.x);
        this.y = p.y + (this.y - anchor.y);
        return this;
    }
    moveXTo(x, anchor = this.leftTop()) {
        this.x = x + (this.x - anchor.x);
        return this;
    }
    moveYTo(y, anchor = this.leftTop()) {
        this.y = y + (this.y - anchor.y);
        return this;
    }
    translate(offsetX, offsetY) {
        this.left += offsetX;
        this.top += offsetY;
        this.right += offsetX;
        this.bottom += offsetY;
        return this;
    }
    translateX(offsetX) {
        return this.translate(offsetX, 0);
    }
    translateY(offsetY) {
        return this.translate(0, offsetY);
    }
    scale(factor) {
        this.left *= factor;
        this.top *= factor;
        this.right *= factor;
        this.bottom *= factor;
        return this;
    }
    flipX(yAxis) {
        const offsetY = 2 * (this.top - yAxis) + this.height;
        return this.translate(0, -offsetY);
    }
    flipY(xAxis) {
        const offsetX = 2 * (this.left - xAxis) + this.width;
        return this.translate(-offsetX, 0);
    }
    flip(point) {
        return this
            .flipX(point.y)
            .flipY(point.x);
    }
    map(fn) {
        this.left = fn.call(this, this.left);
        this.top = fn.call(this, this.top);
        this.right = fn.call(this, this.right);
        this.bottom = fn.call(this, this.bottom);
        return this;
    }
    isBelow(p) {
        return this.top >= p.y;
    }
    isAbove(p) {
        return this.bottom <= p.y;
    }
    isOnTheLeft(p) {
        return this.right <= p.x;
    }
    isOnTheRight(p) {
        return this.left >= p.x;
    }
    position() {
        return this.leftTop();
    }
    leftTop() {
        return Point.create(this.left, this.top);
    }
    centerTop() {
        return Point.create(this.left + (this.width / 2), this.top);
    }
    rightTop() {
        return Point.create(this.right, this.top);
    }
    leftCenter() {
        const x = this.x;
        const y = this.y + (this.height / 2);
        return Point.create(x, y);
    }
    center() {
        const x = this.x + (this.width / 2);
        const y = this.y + (this.height / 2);
        return Point.create(x, y);
    }
    rightCenter() {
        const x = this.x + this.width;
        const y = this.y + (this.height / 2);
        return Point.create(x, y);
    }
    leftBottom() {
        return Point.create(this.left, this.bottom);
    }
    centerBottom() {
        return Point.create(this.left + (this.width / 2), this.bottom);
    }
    rightBottom() {
        return Point.create(this.right, this.bottom);
    }
    isEmpty() {
        return this.width <= 0 || this.height <= 0;
    }
    containsRect(other) {
        if (other.isEmpty()) {
            return true;
        }
        if (this.isEmpty()) {
            return false;
        }
        return other.left >= this.left &&
            other.top >= this.top &&
            other.right <= this.right &&
            other.bottom <= this.bottom;
    }
    containsPoint(point) {
        if (this.isEmpty()) {
            return false;
        }
        return point.x >= this.left &&
            point.x <= this.right &&
            point.y >= this.top &&
            point.y <= this.bottom;
    }
    overflows(other) {
        return this.overflowsLeft(other) ||
            this.overflowsTop(other) ||
            this.overflowsRight(other) ||
            this.overflowsBottom(other);
    }
    overflowsLeft(other) {
        return this.overflow(other).left > 0;
    }
    overflowsTop(other) {
        return this.overflow(other).top > 0;
    }
    overflowsRight(other) {
        return this.overflow(other).right > 0;
    }
    overflowsBottom(other) {
        return this.overflow(other).bottom > 0;
    }
    overflow(other) {
        return Overflow.create(Math.max(0, other.left - this.left), Math.max(0, other.top - this.top), Math.max(0, this.right - other.right), Math.max(0, this.bottom - other.bottom));
    }
    intersects(other) {
        if (this.isEmpty() || other.isEmpty()) {
            return false;
        }
        return this.left < other.right &&
            this.right > other.left &&
            this.top < other.bottom &&
            this.bottom > other.top;
    }
    intersect(other) {
        return this
            .clone()
            .restrictTo(other);
    }
    union(other) {
        return this
            .clone()
            .expandToContain(other);
    }
    restrictTo(other) {
        if (this.isEmpty() || other.isEmpty()) {
            return this.setRectangle(0, 0, 0, 0);
        }
        const left = Math.max(this.left, other.left);
        const top = Math.max(this.top, other.top);
        const right = Math.min(this.right, other.right);
        const bottom = Math.min(this.bottom, other.bottom);
        return this.setRectangle(left, top, Math.max(0, right - left), Math.max(0, bottom - top));
    }
    expandToContain(other) {
        if (this.isEmpty()) {
            return this.copyFrom(other);
        }
        if (other.isEmpty()) {
            return this;
        }
        const left = Math.min(this.left, other.left);
        const top = Math.min(this.top, other.top);
        const right = Math.max(this.right, other.right);
        const bottom = Math.max(this.bottom, other.bottom);
        return this.setRectangle(left, top, right - left, bottom - top);
    }
    translateInside(other) {
        return this
            .translateXInside(other)
            .translateYInside(other);
    }
    translateXInside(other) {
        let offsetX = 0;
        if (this.left <= other.left) {
            offsetX = other.left - this.left;
        }
        if (this.right >= other.right) {
            offsetX = other.right - this.right;
        }
        return this.translate(offsetX, 0);
    }
    translateYInside(other) {
        let offsetY = 0;
        if (this.top <= other.top) {
            offsetY = other.top - this.top;
        }
        if (this.bottom >= other.bottom) {
            offsetY = other.bottom - this.bottom;
        }
        return this.translate(0, offsetY);
    }
    blend(rect, scalar) {
        return Rectangle.fromBounds(this.left + (rect.left - this.left) * scalar, this.top + (rect.top - this.top) * scalar, this.right + (rect.right - this.right) * scalar, this.bottom + (rect.bottom - this.bottom) * scalar);
    }
    inflate(xScale, yScale = xScale) {
        const xAdjust = (this.width * xScale - this.width) / 2;
        const yAdjust = (this.height * yScale - this.height) / 2;
        this.left -= xAdjust;
        this.right += xAdjust;
        this.top -= yAdjust;
        this.bottom += yAdjust;
        return this;
    }
    expandToIntegers() {
        this.left = Math.floor(this.left);
        this.top = Math.floor(this.top);
        this.right = Math.ceil(this.right);
        this.bottom = Math.ceil(this.bottom);
        return this;
    }
    relativeTo(parent) {
        return Rectangle.create(this.left - parent.left, this.top - parent.top, this.width, this.height);
    }
    equals(other) {
        return (this.isEmpty() && other.isEmpty() ||
            this.top === other.top &&
                this.left === other.left &&
                this.bottom === other.bottom &&
                this.right === other.right);
    }
    toString() {
        return `(${this.x}, ${this.y}, ${this.width}, ${this.height})`;
    }
}

class RectangleFactory {
    static fromHtmlElement(el) {
        const w = this.getWindow();
        return Rectangle.create(el.getBoundingClientRect().left + w.pageXOffset, el.getBoundingClientRect().top + w.pageYOffset, el.offsetWidth, el.offsetHeight);
    }
    static fromSvgElement(el) {
        const w = this.getWindow();
        return Rectangle.create(el.getBoundingClientRect().left + w.pageXOffset, el.getBoundingClientRect().top + w.pageYOffset, el.clientWidth, el.clientHeight);
    }
    static fromWindow() {
        const w = this.getWindow();
        return Rectangle.create(w.pageXOffset, w.pageYOffset, w.document.documentElement.clientWidth, w.document.documentElement.clientHeight);
    }
    static getWindow() {
        return window;
    }
}

class PlacementService {
    constructor(placementStrategies = []) {
        this.placementStrategies = placementStrategies;
    }
    static getEffectiveOptions(options) {
        return {
            anchor: Rectangle.empty(),
            element: Rectangle.empty(),
            placementId: 'bottom',
            parent: RectangleFactory.fromWindow(),
            offsetAlong: 0,
            offsetAcross: 0,
            constrainToParent: true,
            flip: true,
            ...options
        };
    }
    static pickPlacementStrategy(placementStrategies, placementId) {
        function matchStrategy(strategy) {
            return strategy.getId() === placementId;
        }
        return (placementStrategies || []).find(matchStrategy);
    }
    place(options) {
        const effectiveOptions = PlacementService.getEffectiveOptions(options);
        const placementStrategy = PlacementService.pickPlacementStrategy(this.placementStrategies, effectiveOptions.placementId);
        if (!placementStrategy) {
            throw new Error('Placement not supported: ' + effectiveOptions.placementId);
        }
        return placementStrategy.calculate(effectiveOptions);
    }
}

class LeftPlacementStrategy {
    getId() {
        return 'left';
    }
    calculate(options) {
        const placedRect = options.element
            .clone()
            .moveTo(options.anchor.leftCenter(), options.element.rightCenter())
            .translateX(-options.offsetAlong)
            .translateY(options.offsetAcross);
        if (options.flip && placedRect.overflowsLeft(options.parent)) {
            placedRect.flipY(options.anchor.center().x);
        }
        if (options.constrainToParent) {
            placedRect.translateInside(options.parent);
        }
        return placedRect;
    }
}

class TopPlacementStrategy {
    getId() {
        return 'top';
    }
    calculate(options) {
        const placedRect = options.element
            .clone()
            .moveTo(options.anchor.centerTop(), options.element.centerBottom())
            .translateY(-options.offsetAlong)
            .translateX(options.offsetAcross);
        if (options.flip && placedRect.overflowsTop(options.parent)) {
            placedRect.flipX(options.anchor.center().y);
        }
        if (options.constrainToParent) {
            placedRect.translateInside(options.parent);
        }
        return placedRect;
    }
}

class BottomPlacementStrategy {
    getId() {
        return 'bottom';
    }
    calculate(options) {
        const placedRect = options.element
            .clone()
            .moveTo(options.anchor.centerBottom(), options.element.centerTop())
            .translateY(options.offsetAlong)
            .translateX(options.offsetAcross);
        if (options.flip && placedRect.overflowsBottom(options.parent)) {
            placedRect.flipX(options.anchor.center().y);
        }
        if (options.constrainToParent) {
            placedRect.translateInside(options.parent);
        }
        return placedRect;
    }
}

class RightPlacementStrategy {
    getId() {
        return 'right';
    }
    calculate(options) {
        const placedRect = options.element
            .clone()
            .moveTo(options.anchor.rightCenter(), options.element.leftCenter())
            .translateX(options.offsetAlong)
            .translateY(options.offsetAcross);
        if (options.flip && placedRect.overflowsRight(options.parent)) {
            placedRect.flipY(options.anchor.center().x);
        }
        if (options.constrainToParent) {
            placedRect.translateInside(options.parent);
        }
        return placedRect;
    }
}

class BottomLeftPlacementStrategy {
    getId() {
        return 'bottom left';
    }
    calculate(options) {
        const placedRect = options.element
            .clone()
            .moveTo(options.anchor.leftBottom(), options.element.leftTop())
            .translateY(options.offsetAlong)
            .translateX(options.offsetAcross);
        if (options.flip && placedRect.overflowsBottom(options.parent)) {
            placedRect.flipX(options.anchor.center().y);
        }
        if (options.constrainToParent) {
            placedRect.translateInside(options.parent);
        }
        return placedRect;
    }
}

function placementServiceFactory() {
    return new PlacementService([
        new LeftPlacementStrategy(),
        new TopPlacementStrategy(),
        new BottomPlacementStrategy(),
        new RightPlacementStrategy(),
        new BottomLeftPlacementStrategy()
    ]);
}
class GeometryModule {
}
/** @nocollapse */ GeometryModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: GeometryModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
/** @nocollapse */ GeometryModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.0.3", ngImport: i0, type: GeometryModule, imports: [CommonModule] });
/** @nocollapse */ GeometryModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: GeometryModule, providers: [
        { provide: PlacementService, useFactory: placementServiceFactory },
    ], imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: GeometryModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    exports: [],
                    declarations: [],
                    providers: [
                        { provide: PlacementService, useFactory: placementServiceFactory },
                    ]
                }]
        }] });

class Offset {
    constructor(x, y) {
        this.x = x;
        this.y = y;
    }
    static create(x, y) {
        return new Offset(x, y);
    }
    equals(other) {
        return this.x === other.x &&
            this.y === other.y;
    }
    toString() {
        return `(${this.x}, ${this.y})`;
    }
}

/**
 * Generated bundle index. Do not edit.
 */

export { GeometryModule, Offset, Overflow, PlacementService, Point, Rectangle, RectangleFactory, placementServiceFactory };
